import {
  Box,
  Heading,
  VStack,
  HStack,
  Input,
  Textarea,
  Button,
  Icon,
  Text,
  Badge,
  Flex,
  SimpleGrid,
  InputGroup,
  InputLeftElement,
  useColorModeValue,
  Link,
  Select,
  Image,
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import {
  FaPaperPlane,
  FaUser,
  FaEnvelope,
  FaHeart,
  FaHospital,
  FaExternalLinkAlt,
} from "react-icons/fa";
import { useState } from "react";
import config from "config";

const MotionBox = motion(Box);
const MotionHeading = motion(Heading);

function Contact() {
  const [subject, setSubject] = useState("General Information");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");

  const maxChars = 200;

  const subjects = [
    { id: "General Information", desc: "General platform inquiries, feedback, or data access assistance." },
    { id: "Scientific & Genomic Inquiry", desc: "Questions regarding genomic data, variants, and research collaboration." },
    { id: "Clinical Collaboration", desc: "Inquiries from clinicians, hospitals, and medical practitioners." },
    { id: "Patient & Family Support", desc: "Connecting with patient advocacy groups and NGO support services." },
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    const contactEmail = config.CONTACT_EMAIL || "tunisian.xp.database@gmail.com";
    const mailtoLink = `mailto:${contactEmail}?subject=[${encodeURIComponent(
      subject
    )}] Message from ${encodeURIComponent(name)}&body=Name: ${encodeURIComponent(
      name
    )}%0AEmail: ${encodeURIComponent(email)}%0ASubject: ${encodeURIComponent(
      subject
    )}%0A%0AMessage:%0A${encodeURIComponent(message)}`;
    window.location.href = mailtoLink;
  };

  const cardBg = useColorModeValue("rgba(255, 255, 255, 0.8)", "rgba(15, 23, 42, 0.8)");
  const cardBorder = useColorModeValue("slate.200", "whiteAlpha.100");
  const headingColor = useColorModeValue("slate.800", "white");
  const textColor = useColorModeValue("slate.600", "slate.300");
  const inputBg = useColorModeValue("white", "navy.900");
  const selectOptionBg = useColorModeValue("#ffffff", "#1e293b");
  const selectOptionColor = useColorModeValue("#1e293b", "#f8fafc");

  return (
    <Box id="contact" as="section" py={{ base: 12, md: 16 }} position="relative" overflow="hidden">
      {/* Ambient Radial Background Glows */}
      <Box
        position="absolute"
        bottom="-10%"
        left="-5%"
        w="400px"
        h="400px"
        borderRadius="full"
        bg="radial-gradient(circle, rgba(13, 148, 136, 0.15) 0%, rgba(0,0,0,0) 70%)"
        pointerEvents="none"
      />
      <Box
        position="absolute"
        top="10%"
        right="-5%"
        w="350px"
        h="350px"
        borderRadius="full"
        bg="radial-gradient(circle, rgba(6, 182, 212, 0.12) 0%, rgba(0,0,0,0) 70%)"
        pointerEvents="none"
      />

      <Box mb={10} textAlign="center">
        <MotionHeading
          size="xl"
          color={headingColor}
          fontWeight="900"
          letterSpacing="-0.02em"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          Contact{" "}
          <Text as="span" bgGradient="linear(to-r, teal.500, cyan.400)" bgClip="text">
            Us
          </Text>
        </MotionHeading>
        <Text color={textColor} fontSize="sm" maxW="600px" mx="auto" mt={2}>
          Connect with researchers, reach out to partner hospital services, or contact the patient support association.
        </Text>
      </Box>

      <SimpleGrid
        columns={{ base: 1, lg: 2 }}
        spacing={8}
        maxW="1180px"
        mx="auto"
        px={6}
        alignItems="stretch"
      >
        {/* Left Column: Association, Hospital Services & Donation */}
        <VStack spacing={5} align="stretch" justify="space-between">
          {/* Association Card */}
          <MotionBox
            bg={cardBg}
            backdropFilter="blur(24px)"
            p={{ base: 5, md: 6 }}
            borderRadius="24px"
            border="1px solid"
            borderColor={cardBorder}
            shadow="sm"
            whileHover={{ y: -3, shadow: "md" }}
            transition={{ duration: 0.2 }}
          >
            <Flex justify="space-between" align="start" mb={2}>
              <HStack spacing={3}>
                <Flex
                  w="52px"
                  h="52px"
                  borderRadius="16px"
                  overflow="hidden"
                  bg="white"
                  p="2px"
                  border="1px solid"
                  borderColor="teal.200"
                  align="center"
                  justify="center"
                  flexShrink={0}
                  shadow="sm"
                >
                  <Image
                    src="/imgs/team/enfantsdelalune.jpg"
                    alt="Logo Enfants de la Lune"
                    w="100%"
                    h="100%"
                    objectFit="contain"
                  />
                </Flex>
                <Box>
                  <Text fontSize="xs" fontWeight="800" color="teal.500" textTransform="uppercase" letterSpacing="0.06em">
                    Patient Association / NGO
                  </Text>
                  <Heading size="sm" color={headingColor}>
                    L'Association d'aide aux enfants atteints de Xeroderma Pigmentosum
                  </Heading>
                </Box>
              </HStack>
            </Flex>
            <Link
              href="https://arab.org/directory/aide-aux-enfants-atteints-de-xeroderma-pigmentosum-enfants-de-la-lune-tunisie/"
              isExternal
              fontSize="xs"
              fontWeight="700"
              color="teal.500"
              display="inline-flex"
              alignItems="center"
              gap="6px"
              _hover={{ textDecoration: "underline" }}
            >
              <Text>Visit Association Directory Profile</Text>
              <Icon as={FaExternalLinkAlt} boxSize="10px" />
            </Link>
          </MotionBox>

          {/* Hospital Dermatology Services Card */}
          <MotionBox
            bg={cardBg}
            backdropFilter="blur(24px)"
            p={{ base: 5, md: 6 }}
            borderRadius="24px"
            border="1px solid"
            borderColor={cardBorder}
            shadow="sm"
            whileHover={{ y: -3, shadow: "md" }}
            transition={{ duration: 0.2 }}
          >
            <HStack spacing={3} mb={3}>
              <Flex
                w="46px"
                h="46px"
                borderRadius="14px"
                bg="cyan.50"
                color="cyan.600"
                align="center"
                justify="center"
                _dark={{ bg: "whiteAlpha.100", color: "cyan.300" }}
              >
                <Icon as={FaHospital} boxSize="22px" />
              </Flex>
              <Box>
                <Text fontSize="xs" fontWeight="800" color="cyan.600" textTransform="uppercase" letterSpacing="0.06em">
                  Clinical Partner Services
                </Text>
                <Heading size="sm" color={headingColor}>
                  Services de Dermatologie et Vénéréologie
                </Heading>
              </Box>
            </HStack>
            <VStack spacing={2.5} align="stretch" fontSize="xs">
              <Box
                as="a"
                href="http://www.chularabta.tn/offre-de-soins/dermatologie/"
                target="_blank"
                rel="noopener noreferrer"
                p={3}
                bg={useColorModeValue("blackAlpha.50", "whiteAlpha.50")}
                borderRadius="12px"
                border="1px solid transparent"
                transition="all 0.2s"
                _hover={{
                  borderColor: "cyan.400",
                  bg: useColorModeValue("cyan.50", "whiteAlpha.100"),
                  transform: "translateX(3px)",
                  textDecoration: "none",
                }}
              >
                <Flex justify="space-between" align="center">
                  <Box>
                    <Text fontWeight="800" color={headingColor} fontSize="sm">
                      Service de dermatologie – Hôpital La Rabta
                    </Text>
                  </Box>
                  <Icon as={FaExternalLinkAlt} color="cyan.500" boxSize="11px" ml={2} />
                </Flex>
              </Box>

              <Box
                as="a"
                href="http://www.chucharlesnicolle.tn/service-dermatologie/"
                target="_blank"
                rel="noopener noreferrer"
                p={3}
                bg={useColorModeValue("blackAlpha.50", "whiteAlpha.50")}
                borderRadius="12px"
                border="1px solid transparent"
                transition="all 0.2s"
                _hover={{
                  borderColor: "cyan.400",
                  bg: useColorModeValue("cyan.50", "whiteAlpha.100"),
                  transform: "translateX(3px)",
                  textDecoration: "none",
                }}
              >
                <Flex justify="space-between" align="center">
                  <Box>
                    <Text fontWeight="800" color={headingColor} fontSize="sm">
                      Service de dermatologie – Hôpital Charles Nicolle
                    </Text>
                  </Box>
                  <Icon as={FaExternalLinkAlt} color="cyan.500" boxSize="11px" ml={2} />
                </Flex>
              </Box>

              <Box
                as="a"
                href="http://www.hopitalhabibthameur.tn/service-de-dermatologie/"
                target="_blank"
                rel="noopener noreferrer"
                p={3}
                bg={useColorModeValue("blackAlpha.50", "whiteAlpha.50")}
                borderRadius="12px"
                border="1px solid transparent"
                transition="all 0.2s"
                _hover={{
                  borderColor: "cyan.400",
                  bg: useColorModeValue("cyan.50", "whiteAlpha.100"),
                  transform: "translateX(3px)",
                  textDecoration: "none",
                }}
              >
                <Flex justify="space-between" align="center">
                  <Box>
                    <Text fontWeight="800" color={headingColor} fontSize="sm">
                      Service de dermatologie – Hôpital Habib Thameur
                    </Text>
                  </Box>
                  <Icon as={FaExternalLinkAlt} color="cyan.500" boxSize="11px" ml={2} />
                </Flex>
              </Box>
            </VStack>
          </MotionBox>

          {/* Donation Callout Card */}
          <MotionBox
            bgGradient={useColorModeValue(
              "linear(to-br, pink.50, teal.50)",
              "linear(to-br, rgba(236, 72, 153, 0.15), rgba(13, 148, 136, 0.15))"
            )}
            p={{ base: 4, md: 5 }}
            borderRadius="24px"
            border="1.5px solid"
            borderColor={useColorModeValue("pink.300", "pink.600")}
            shadow="sm"
            whileHover={{ y: -3, shadow: "md" }}
            transition={{ duration: 0.2 }}
          >
            <Flex
              direction={{ base: "column", xl: "row" }}
              justify="space-between"
              align={{ base: "stretch", xl: "center" }}
              gap={3}
            >
              <HStack spacing={3} align="center">
                <Flex
                  w="42px"
                  h="42px"
                  borderRadius="14px"
                  bg="pink.500"
                  color="white"
                  align="center"
                  justify="center"
                  shadow="0 4px 12px rgba(236, 72, 153, 0.4)"
                  flexShrink={0}
                >
                  <Icon as={FaHeart} boxSize="18px" />
                </Flex>
                <Box>
                  <Heading size="sm" color={headingColor}>
                    Support XP Patients
                  </Heading>
                  <Text fontSize="xs" color={textColor} opacity={0.85} mt="2px">
                    Direct NGO assistance for moon children
                  </Text>
                </Box>
              </HStack>

              <Button
                as={Link}
                href="https://arab.org/directory/aide-aux-enfants-atteints-de-xeroderma-pigmentosum-enfants-de-la-lune-tunisie/"
                isExternal
                size="sm"
                colorScheme="pink"
                bgGradient="linear(to-r, pink.500, red.400)"
                color="white"
                borderRadius="full"
                px={5}
                py={2.5}
                h="auto"
                minH="36px"
                w={{ base: "100%", xl: "auto" }}
                whiteSpace="normal"
                textAlign="center"
                leftIcon={<FaHeart />}
                _hover={{
                  bgGradient: "linear(to-r, pink.600, red.500)",
                  textDecoration: "none",
                  shadow: "0 6px 20px rgba(236, 72, 153, 0.4)",
                }}
              >
                Make a Donation (Faire un Don)
              </Button>
            </Flex>
          </MotionBox>
        </VStack>

        {/* Right Column: Send a Message Form */}
        <MotionBox
          bg={cardBg}
          backdropFilter="blur(24px)"
          p={{ base: 6, md: 8 }}
          borderRadius="32px"
          border="1px solid"
          borderColor={cardBorder}
          shadow={useColorModeValue("0 20px 40px rgba(0,0,0,0.08)", "0 20px 50px rgba(0,0,0,0.4)")}
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <VStack spacing={5} as="form" onSubmit={handleSubmit} align="stretch">
            {/* Header */}
            <Box mb={1}>
              <HStack spacing={3} mb={1}>
                <Flex
                  w="42px"
                  h="42px"
                  borderRadius="14px"
                  bgGradient="linear(to-br, teal.400, cyan.500)"
                  color="white"
                  align="center"
                  justify="center"
                  shadow="0 4px 12px rgba(13, 148, 136, 0.3)"
                >
                  <Icon as={FaPaperPlane} boxSize="18px" />
                </Flex>
                <Box>
                  <Heading size="md" color={headingColor} fontWeight="800">
                    Send a Message
                  </Heading>
                  <Text fontSize="xs" color={textColor}>
                    Have a question or collaboration proposal? Reach out directly.
                  </Text>
                </Box>
              </HStack>
            </Box>

            {/* Name Input */}
            <Box>
              <Text fontSize="xs" fontWeight="700" color={headingColor} mb={1.5}>
                Your Name
              </Text>
              <InputGroup size="md">
                <InputLeftElement pointerEvents="none">
                  <Icon as={FaUser} color="teal.400" />
                </InputLeftElement>
                <Input
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Full name or clinician name"
                  fontSize="sm"
                  borderRadius="14px"
                  bg={inputBg}
                  borderColor={cardBorder}
                  _focus={{
                    borderColor: "teal.400",
                    boxShadow: "0 0 15px rgba(13, 148, 136, 0.25)",
                  }}
                />
              </InputGroup>
            </Box>

            {/* Email Input */}
            <Box>
              <Text fontSize="xs" fontWeight="700" color={headingColor} mb={1.5}>
                Your Email
              </Text>
              <InputGroup size="md">
                <InputLeftElement pointerEvents="none">
                  <Icon as={FaEnvelope} color="teal.400" />
                </InputLeftElement>
                <Input
                  required
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="name@institution.tn"
                  fontSize="sm"
                  borderRadius="14px"
                  bg={inputBg}
                  borderColor={cardBorder}
                  _focus={{
                    borderColor: "teal.400",
                    boxShadow: "0 0 15px rgba(13, 148, 136, 0.25)",
                  }}
                />
              </InputGroup>
            </Box>

            {/* Subject Selector */}
            <Box>
              <Text fontSize="xs" fontWeight="700" color={headingColor} mb={1.5}>
                Inquiry Topic
              </Text>
              <Select
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                fontSize="sm"
                borderRadius="14px"
                bg={inputBg}
                borderColor={cardBorder}
                _focus={{
                  borderColor: "teal.400",
                  boxShadow: "0 0 15px rgba(13, 148, 136, 0.25)",
                }}
              >
                {subjects.map((s) => (
                  <option
                    key={s.id}
                    value={s.id}
                    style={{ background: selectOptionBg, color: selectOptionColor }}
                  >
                    {s.id}
                  </option>
                ))}
              </Select>
              <Text fontSize="xs" color="slate.400" mt={1} pl={1}>
                {subjects.find((s) => s.id === subject)?.desc}
              </Text>
            </Box>

            {/* Message Textarea */}
            <Box>
              <Flex justify="space-between" align="center" mb={1.5}>
                <Text fontSize="xs" fontWeight="700" color={headingColor}>
                  Your Message
                </Text>
                <Text
                  fontSize="xs"
                  fontWeight="700"
                  color={message.length >= maxChars ? "red.500" : "teal.500"}
                >
                  {message.length} / {maxChars} characters
                </Text>
              </Flex>
              <Textarea
                required
                value={message}
                maxLength={maxChars}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Type your message here (up to 200 characters)..."
                fontSize="sm"
                borderRadius="14px"
                bg={inputBg}
                borderColor={cardBorder}
                rows={4}
                p={3}
                _focus={{
                  borderColor: "teal.400",
                  boxShadow: "0 0 15px rgba(13, 148, 136, 0.25)",
                }}
              />
            </Box>

            {/* Submit Button */}
            <Button
              type="submit"
              size="lg"
              colorScheme="teal"
              bgGradient="linear(to-r, teal.500, cyan.500)"
              color="white"
              borderRadius="full"
              w="full"
              py="22px"
              fontSize="md"
              fontWeight="700"
              shadow="0 10px 25px rgba(13, 148, 136, 0.35)"
              rightIcon={<FaPaperPlane size="14" />}
              _hover={{
                bgGradient: "linear(to-r, teal.600, cyan.600)",
                shadow: "0 14px 30px rgba(13, 148, 136, 0.5)",
              }}
            >
              Send Message
            </Button>
          </VStack>
        </MotionBox>
      </SimpleGrid>
    </Box>
  );
}

export default Contact;
